import React from 'react';
import * as S from './CatalogueListItem.styles';
import { Category } from '@/api/types/categories.types';
import {
  ALL_CATEGORIES_ID,
  ALL_SHOPS_ID,
} from '@/components/CatalogueGrid/CatalogueGrid.constants';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

interface CatalogueListItemProps {
  item?: Category;
  onClick?: () => void;
  isHorizontalView?: boolean;
  isStoresIcon?: boolean;
  isLoading?: boolean;
  className?: string;
  hideOnDesktop?: boolean;
}

export const CatalogueListItem: React.FC<CatalogueListItemProps> = ({
  item,
  onClick,
  isHorizontalView,
  isStoresIcon,
  isLoading = false,
  hideOnDesktop,
  ...props
}) => {
  const { id, name, path, description, icon } = item ?? {};

  const isAllCategoriesItem = id === ALL_CATEGORIES_ID;
  const isDynamicCategory = id !== ALL_CATEGORIES_ID && id !== ALL_SHOPS_ID;

  function handleClick(event?: React.MouseEvent) {
    if (isLoading || !id) return;

    if (isDynamicCategory) {
      event?.preventDefault();
    }

    onClick?.();
  }

  function getTexts() {
    if (isHorizontalView) {
      return isLoading ? (
        <S.HorizontalTitleLoaderContainer>
          <SkeletonLoader height={13} width={60} borderRadius={20} />
          <SkeletonLoader height={13} width={30} borderRadius={20} />
        </S.HorizontalTitleLoaderContainer>
      ) : (
        <S.GridItemTitle $variant="3">{name}</S.GridItemTitle>
      );
    }

    return isLoading ? (
      <>
        <S.TextSkeletonLoader width="100px" height="18px" />
        <S.TextSkeletonLoader height="14.4px" />
      </>
    ) : (
      <>
        <S.ItemTitle $variant="1">{name}</S.ItemTitle>
        <S.ItemDescription>{description}</S.ItemDescription>
      </>
    );
  }

  return (
    <>
      <S.Box
        {...props}
        data-testid="catalogue-list-item"
        onClick={handleClick}
        $isHorizontalView={isHorizontalView}
        suppressHydrationWarning
        $hideOnDesktop={hideOnDesktop}
        {...(!isAllCategoriesItem &&
          !isLoading && {
            as: CustomLink,
            href: `/${path?.replace(/^\//, '')}`,
            dataTestId: 'catalogue-list-item',
          })}
      >
        {isLoading ? (
          <S.ImageSkeletonLoader
            dataTestId="catalogue-list-item-image-skeleton-loader"
            $isHorizontalView={isHorizontalView}
          />
        ) : (
          <S.ImgBox
            $isHorizontalView={isHorizontalView}
            $isAllCategoriesItem={isAllCategoriesItem}
            $isStoresIcon={isStoresIcon}
          >
            <img
              src={isDynamicCategory ? icon : `/images/${icon}`}
              alt={name}
            />
          </S.ImgBox>
        )}
        <S.Texts
          {...(isLoading &&
            !isHorizontalView && {
              'data-testid': 'catalogue-list-item-texts-skeleton-loader',
            })}
        >
          {getTexts()}
        </S.Texts>
        {!isHorizontalView && !isLoading && <S.CategoryChevronIcon />}
      </S.Box>
    </>
  );
};
